import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  Clock,
  CreditCard,
  FileText,
  Landmark,
  PieChart,
  TrendingUp
} from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import { db } from '../../../firebase/config';
import { useCurrency } from '../../../utils/currencyUtils';
import { AppContext } from '../../../context/AppContext';
import { buildFinanceModel, getDateRange } from '../financeReporting';

const ReportsList = () => {
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { formatAmount } = useCurrency();

  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [datePreset, setDatePreset] = useState('month');
  const [selectedBusinessId, setSelectedBusinessId] = useState('all');

  useEffect(() => {
    if (activeBusiness?.id) {
      setSelectedBusinessId(activeBusiness.id);
    }
  }, [activeBusiness?.id]);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.uid) return;
      setLoading(true);
      try {
        const [businessSnap, accountSnap, transactionSnap] = await Promise.all([
          getDocs(query(collection(db, 'businesses'), where('createdBy', '==', currentUser.uid))),
          getDocs(query(collection(db, 'accounts'), where('createdBy', '==', currentUser.uid))),
          getDocs(query(collection(db, 'financialTransactions'), where('createdBy', '==', currentUser.uid)))
        ]);

        setBusinesses(businessSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setAccounts(accountSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setTransactions(transactionSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error('Error loading reports list:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser?.uid]);

  const { start, end } = getDateRange(datePreset);

  const filteredAccounts = useMemo(() => {
    if (selectedBusinessId === 'all') return accounts;
    return accounts.filter((account) => account.businessId === selectedBusinessId);
  }, [accounts, selectedBusinessId]);

  const filteredTransactions = useMemo(() => {
    if (selectedBusinessId === 'all') return transactions;
    return transactions.filter((tx) => tx.businessId === selectedBusinessId);
  }, [transactions, selectedBusinessId]);

  const model = useMemo(() => (
    buildFinanceModel({
      transactions: filteredTransactions,
      accounts: filteredAccounts,
      rangeStart: start,
      rangeEnd: end,
      now: new Date()
    })
  ), [filteredTransactions, filteredAccounts, start, end]);

  const reportCards = [
    {
      id: 'profit-loss',
      name: 'Profit & Loss',
      description: 'Revenue, expenses, and net profit for the selected period.',
      icon: <PieChart size={22} className="text-blue-600" />,
      path: '/finance/reports/profit-loss',
      metricLabel: 'Net Profit',
      metricValue: model.current.netProfit
    },
    {
      id: 'balance-sheet',
      name: 'Balance Sheet',
      description: 'Assets, liabilities, and equity snapshot.',
      icon: <Landmark size={22} className="text-green-600" />,
      path: '/finance/reports/balance-sheet',
      metricLabel: 'Equity',
      metricValue: model.balanceSheet.equity
    },
    {
      id: 'cash-flow',
      name: 'Cash Flow',
      description: 'Cash in, cash out, and period net cash movement.',
      icon: <TrendingUp size={22} className="text-indigo-600" />,
      path: '/finance/reports/cash-flow',
      metricLabel: 'Net Cash Flow',
      metricValue: model.cash.net
    },
    {
      id: 'receivables',
      name: 'Accounts Receivable Aging',
      description: 'Outstanding customer invoices and overdue amounts.',
      icon: <Clock size={22} className="text-amber-600" />,
      path: '/finance/reports/receivables',
      metricLabel: 'Outstanding',
      metricValue: model.receivables.outstanding
    },
    {
      id: 'payables',
      name: 'Accounts Payable Aging',
      description: 'Outstanding supplier bills and due exposure.',
      icon: <CreditCard size={22} className="text-rose-600" />,
      path: '/finance/reports/payables',
      metricLabel: 'Outstanding',
      metricValue: model.payables.outstanding
    },
    {
      id: 'tax',
      name: 'Tax Summary',
      description: 'Collected tax, paid tax, and estimated liability.',
      icon: <FileText size={22} className="text-gray-700" />,
      path: '/finance/reports/tax',
      metricLabel: 'Tax Liability',
      metricValue: model.tax.liability
    },
    {
      id: 'expenses',
      name: 'Expense Analysis',
      description: 'Expense categories and spend concentration.',
      icon: <BarChart3 size={22} className="text-purple-600" />,
      path: '/finance/reports/expenses',
      metricLabel: 'Total Expenses',
      metricValue: model.current.expenses
    }
  ];

  const formatDueDays = (date) => {
    if (!date) return 'N/A';
    const ms = new Date().getTime() - date.getTime();
    const days = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
    return `${days} days`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-56" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-32 bg-gray-100 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Financial Reports</h2>
            <p className="text-sm text-gray-500 mt-1">
              Core business reports for {start.toLocaleDateString()} - {end.toLocaleDateString()}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={selectedBusinessId}
              onChange={(event) => setSelectedBusinessId(event.target.value)}
            >
              <option value="all">All businesses</option>
              {businesses.map((business) => (
                <option key={business.id} value={business.id}>
                  {business.name || business.businessName || 'Untitled Business'}
                </option>
              ))}
            </select>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={datePreset}
              onChange={(event) => setDatePreset(event.target.value)}
            >
              <option value="week">This week</option>
              <option value="month">This month</option>
              <option value="quarter">This quarter</option>
              <option value="year">This year</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {reportCards.map((report) => (
          <Link
            key={report.id}
            to={report.path}
            className="bg-white rounded-xl shadow-md border border-gray-100 p-5 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-lg bg-gray-100 flex items-center justify-center">{report.icon}</div>
              <span className="text-xs text-gray-500">{report.metricLabel}</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mt-4">{report.name}</h3>
            <p className="text-sm text-gray-500 mt-1 min-h-[40px]">{report.description}</p>
            <p className="text-2xl font-bold text-gray-900 mt-4">{formatAmount(report.metricValue)}</p>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Top Overdue Receivables</h3>
            <Link to="/finance/reports/receivables?status=overdue" className="text-sm font-medium text-[#190192] hover:underline">
              Full report
            </Link>
          </div>
          {model.receivables.top.length === 0 ? (
            <p className="text-sm text-gray-500">No overdue invoices.</p>
          ) : (
            <div className="space-y-3">
              {model.receivables.top.map((item) => (
                <div key={item.id} className="border border-gray-100 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.documentNumber || item.id}</p>
                    <p className="text-xs text-gray-500">
                      Due {item._dueDate ? item._dueDate.toLocaleDateString() : 'N/A'} | {formatDueDays(item._dueDate)} overdue
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-amber-700">{formatAmount(item.balanceDue || 0)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Top Overdue Payables</h3>
            <Link to="/finance/reports/payables?status=overdue" className="text-sm font-medium text-[#190192] hover:underline">
              Full report
            </Link>
          </div>
          {model.payables.top.length === 0 ? (
            <p className="text-sm text-gray-500">No overdue bills.</p>
          ) : (
            <div className="space-y-3">
              {model.payables.top.map((item) => (
                <div key={item.id} className="border border-gray-100 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.documentNumber || item.id}</p>
                    <p className="text-xs text-gray-500">
                      Due {item._dueDate ? item._dueDate.toLocaleDateString() : 'N/A'} | {formatDueDays(item._dueDate)} overdue
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-rose-700">{formatAmount(item.balanceDue || 0)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Quick Report Shortcuts</h3>
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <Link to="/finance/reports/profit-loss?period=month" className="border border-gray-200 rounded-lg p-3 flex items-center hover:bg-gray-50">
            <Calendar size={16} className="mr-2 text-[#190192]" />
            <span className="text-sm font-medium text-gray-700">This Month P&L</span>
          </Link>
          <Link to="/finance/reports/balance-sheet?period=quarter" className="border border-gray-200 rounded-lg p-3 flex items-center hover:bg-gray-50">
            <Landmark size={16} className="mr-2 text-[#190192]" />
            <span className="text-sm font-medium text-gray-700">Quarter Balance Sheet</span>
          </Link>
          <Link to="/finance/reports/cash-flow?period=month" className="border border-gray-200 rounded-lg p-3 flex items-center hover:bg-gray-50">
            <TrendingUp size={16} className="mr-2 text-[#190192]" />
            <span className="text-sm font-medium text-gray-700">Monthly Cash Flow</span>
          </Link>
          <Link to="/finance/reports/tax?period=year" className="border border-gray-200 rounded-lg p-3 flex items-center hover:bg-gray-50">
            <AlertTriangle size={16} className="mr-2 text-amber-500" />
            <span className="text-sm font-medium text-gray-700">Yearly Tax Summary</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReportsList;
